/* global BigInt */
import { formatUnits } from 'viem';

// USDT amounts are 6 decimals; prices come formatted from the context
export const USDT_DECIMALS = 6;
export const REFUND_DELAY_SECONDS = 120;

export const toUsdt = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  try {
    return Number(formatUnits(BigInt(String(value)), USDT_DECIMALS));
  } catch (e) {
    return 0;
  }
};

export const formatUsdt = (value, digits = 2) =>
  Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const formatPrice = (value) => {
  const n = Number(value);
  if (!n) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatMultiplier = (value) => (value > 0 && Number.isFinite(value) ? `${value.toFixed(2)}x` : '—');

/**
 * Pool of a round, split per side. Side amounts on-chain include the house seed (half per side) and the players'
 * bets weighted by the early-bird bonus; deposits (without the bonus) come from userBullDeposit/userBearDeposit
 * when the contract has them, otherwise the weights are used as an approximation.
 */
export const roundPool = (round, extras = {}) => {
  const houseUp = toUsdt(extras.houseBullAmount);
  const houseDown = toUsdt(extras.houseBearAmount);
  const weightUp = Math.max(0, toUsdt(round?.bullAmount) - houseUp);
  const weightDown = Math.max(0, toUsdt(round?.bearAmount) - houseDown);
  const depositUp = extras.userBullDeposit !== undefined ? toUsdt(extras.userBullDeposit) : weightUp;
  const depositDown = extras.userBearDeposit !== undefined ? toUsdt(extras.userBearDeposit) : weightDown;
  const total = toUsdt(round?.totalAmount) || houseUp + houseDown + depositUp + depositDown;
  const up = houseUp + depositUp;
  const down = houseDown + depositDown;
  return {
    total,
    house: houseUp + houseDown,
    up: { house: houseUp, deposits: depositUp, weight: weightUp, stake: up },
    down: { house: houseDown, deposits: depositDown, weight: weightDown, stake: down },
    upShare: up + down > 0 ? up / (up + down) : 0.5,
  };
};

/**
 * What a bet of `amount` USDT on `side` would receive if that side wins, under the contract's parimutuel rules:
 * the fees are taken once from the round total, the rest is split by everyone on the winning side in proportion to
 * their stake (house seed included), and the players' part is shared by weight (bet x early-bird bonus).
 */
export const estimatePayout = (pool, side, amount, { feeBps = 1000, bonusBps = 0 } = {}) => {
  if (!(amount > 0)) return 0;
  const s = pool[side];
  const total = pool.total + amount;
  const pot = total * (1 - feeBps / 10000);
  const deposits = s.deposits + amount;
  const playersPot = (pot * deposits) / (s.house + deposits);
  const weight = amount * (1 + bonusBps / 10000);
  return (playersPot * weight) / (s.weight + weight);
};

/** Payout per USDT of the players already on `side` (a settled or locked round, no new bets) */
export const sideMultiplier = (pool, side, { feeBps = 1000 } = {}) => {
  const s = pool[side];
  if (!(s.deposits > 0) || !(s.weight > 0)) return 0;
  const pot = pool.total * (1 - feeBps / 10000);
  const playersPot = (pot * s.deposits) / (s.house + s.deposits);
  return playersPot / s.weight;
};

/** Payout per USDT for the next bettor (1 USDT bet), so the multiplier shows even on an empty side */
export const nextMultiplier = (pool, side, config) => estimatePayout(pool, side, 1, config);

export const priceSide = (closeOrCurrent, lock) => {
  const c = Number(closeOrCurrent);
  const l = Number(lock);
  if (!c || !l) return null;
  if (c > l) return 'up';
  if (c < l) return 'down';
  return 'tie';
};

/**
 * Outcome of a finished round: 'up' | 'down' | 'tie' | 'refund' (settled late, everyone gets the bet back minus
 * the fees) | 'awaiting' (not settled yet; becomes a refund once settled more than 2 minutes after the close)
 */
export const roundOutcome = (round, extras = {}, nowSec = Date.now() / 1000) => {
  if (!round) return 'awaiting';
  const close = Number(round.closeTimestamp || 0);
  const executed = Number(round.executedTimestamp || 0);
  if (!round.oracleCalled) {
    return close && nowSec > close + REFUND_DELAY_SECONDS ? 'refund_pending' : 'awaiting';
  }
  if (extras.refunded || (executed && close && executed > close + REFUND_DELAY_SECONDS)) return 'refund';
  return priceSide(round.closePrice, round.lockPrice) || 'tie';
};

/** What the user receives from a settled round (contract formula), 0 when nothing */
export const userPayout = (round, extras, bet, outcome) => {
  if (!bet) return 0;
  const weight = toUsdt(bet.amount);
  const base = toUsdt(round?.rewardBaseCalAmount);
  const reward = toUsdt(round?.rewardAmount);
  if (outcome === 'refund') {
    // refunds go by deposit; contracts without betDeposit refunded by weight
    const share = bet.deposit !== undefined ? toUsdt(bet.deposit) : weight;
    return base > 0 ? (reward * share) / base : share * 0.9;
  }
  if (outcome !== bet.position || !(base > 0)) return 0;
  return (reward * weight) / base;
};
